import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Menu, X, Gamepad2 } from "lucide-react";

const NAV = [
  { to: "/fps-finder", label: "FPS Finder" },
  { to: "/compare", label: "Compare" },
  { to: "/price-index", label: "Price Index" },
  { to: "/guides", label: "Guides" },
  { to: "/glossary", label: "Glossary" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    navigate(`/fps-finder?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-charcoal/90 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-4 px-4 sm:px-6 lg:px-8">
        <Link data-testid="logo-link" to="/" className="flex items-center gap-2 shrink-0">
          <span className="relative flex h-8 w-8 items-center justify-center rounded-lg bg-neon/10 ring-1 ring-neon/40">
            <Gamepad2 className="h-4.5 w-4.5 text-neon" />
            <span className="pulse-dot absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-neon" />
          </span>
          <span className="font-display text-sm font-black leading-none tracking-wider text-white sm:text-base">
            GAMING<span className="text-neon">KITS</span>.ONLINE
          </span>
        </Link>

        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              data-testid={`nav-${n.label.replace(/\s+/g, "-").toLowerCase()}`}
              to={n.to}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "text-neon" : "text-slate-300 hover:text-white"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <form onSubmit={submit} className="ml-auto hidden max-w-xs flex-1 md:block">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-graphite px-3 py-2 focus-within:border-cyber/50">
            <Search className="h-4 w-4 text-slate-500" />
            <input
              data-testid="header-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search phones, chipsets…"
              className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
            />
          </div>
        </form>

        <span className="ml-auto hidden rounded-md border border-border bg-graphite px-2 py-1 font-mono text-xs text-cyber md:ml-2 md:inline">
          PKR
        </span>

        <a
          href="https://phoneflip.pk"
          target="_blank"
          rel="noopener noreferrer nofollow"
          data-testid="header-phoneflip-link"
          className="ml-2 hidden items-center gap-1.5 rounded-md border border-border bg-graphite px-2.5 py-1.5 text-xs font-medium text-slate-300 transition-colors hover:border-neon/40 hover:text-neon md:inline-flex"
        >
          Buy/Sell on PhoneFlip
        </a>

        <button
          data-testid="mobile-menu-toggle"
          className="ml-auto lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6 text-white" /> : <Menu className="h-6 w-6 text-white" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-border bg-charcoal px-4 py-4 lg:hidden">
          <form onSubmit={submit} className="mb-3">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-graphite px-3 py-2">
              <Search className="h-4 w-4 text-slate-500" />
              <input
                data-testid="mobile-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search phones…"
                className="w-full bg-transparent text-sm text-white placeholder:text-slate-500 focus:outline-none"
              />
            </div>
          </form>
          <div className="flex flex-col">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="rounded-md px-2 py-2.5 text-sm font-medium text-slate-200 hover:bg-graphite"
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href="https://phoneflip.pk"
              target="_blank"
              rel="noopener noreferrer nofollow"
              data-testid="mobile-phoneflip-link"
              className="mt-1 rounded-md border border-border bg-graphite px-2 py-2.5 text-sm font-medium text-cyber"
            >
              Buy/Sell on PhoneFlip →
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
